<template>
  <div>
    <div class="d-flex justify-content-between flex-wrap align-items-center mb-4">
      <div>
        <h1>Chatbot Statistics</h1>
      </div>
      <div class="d-flex flex-wrap align-items-center">
        <div class="btn-group mr-3 mb-3" role="group">
          <button
            v-for="range in dateRanges"
            :key="range.value"
            type="button"
            class="btn btn-sm"
            :class="selectedRange === range.value ? 'btn-primary' : 'btn-outline-primary'"
            @click="changeDateRange(range.value)"
          >
            {{ range.label }}
          </button>
        </div>
        <div class="d-flex align-items-center  mb-3">
          <button
            class="btn btn-sm btn-outline-secondary mr-2"
            @click="refreshStats"
            :disabled="loading"
            v-b-tooltip.hover.top
            title="Refresh statistics"
          >
            <i class="fa fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          </button>
          <div v-if="autoRefresh" class="auto-refresh-indicator">
            <small class="text-muted">
              <i class="fa fa-circle text-success blink"></i> Auto-refresh
            </small>
          </div>
        </div>
      </div>
    </div>

    <LoadingIndicator centered v-if="loading" />
    <div  v-if="stats && !loading">
      <div class="row">
        <div class="col-md-4 col-sm-6">
          <StatCard
            label="Active Conversations"
            :value="stats.activeConversations"
            icon="comments"
            color="primary"
            :trend="calculateTrend('activeConversations')"
            tooltip="Number of ongoing chatbot conversations currently in progress"
          />
        </div>
        <div class="col-md-4 col-sm-6">
          <StatCard
            label="Takeovers Today"
            :value="stats.takenOverToday"
            icon="user-shield"
            color="warning"
            :trend="calculateTrend('takenOverToday')"
            tooltip="Number of conversations where an agent manually took over from the chatbot today"
          />
        </div>
        <div class="col-md-4 col-sm-6">
          <StatCard
            label="Converted Today"
            :value="stats.convertedToday"
            icon="check-circle"
            color="success"
            :trend="calculateTrend('convertedToday')"
            tooltip="Number of conversations that successfully converted to leads today"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-4 col-sm-6">
          <StatCard
            label="Closed Today"
            :value="stats.closedToday"
            icon="times-circle"
            color="secondary"
            :trend="calculateTrend('closedToday')"
            tooltip="Number of chatbot conversations that were closed or completed today"
          />
        </div>
        <div class="col-md-4 col-sm-6">
          <StatCard
            label="Average Duration"
            :value="stats.averageDurationMinutes"
            suffix=" min"
            icon="clock"
            color="info"
            :trend="calculateTrend('averageDurationMinutes')"
            tooltip="Average time duration of chatbot conversations in minutes"
          />
        </div>
        <div class="col-md-4 col-sm-6">
          <StatCard
            label="High Intent (above 70%)"
            :value="stats.highIntentConversations"
            icon="star"
            color="danger"
            :trend="calculateTrend('highIntentConversations')"
            tooltip="Conversations with high purchase intent score (above 70%)"
          />
        </div>
      </div>

      <div class="mt-4 d-flex justify-content-end">
        <button class="btn btn-outline-secondary" @click="exportToCSV">
          <i class="fa fa-download"></i> Export CSV
        </button>
      </div>
    </div>

    <EmptyState v-if="!loading && !stats" message="No statistics available" />

  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import EmptyState from '@/components/shared/EmptyState';
import StatCard from '@/components/shared/StatCard';

export default {
  name: 'ChatbotStats',
  components: {
    LoadingIndicator,
    EmptyState,
    StatCard,
  },
  data() {
    return {
      stats: null,
      loading: false,
      autoRefresh: true,
      refreshInterval: null,
      selectedRange: 'today',
      dateRanges: [
        { label: 'Today', value: 'today' },
        { label: 'This Week', value: 'week' },
        { label: 'This Month', value: 'month' },
      ],
    };
  },
  methods: {
    async fetchStats() {
      if (this.loading) return;

      const params = {
        range: this.selectedRange,
      };

      this.loading = true;
      const response = await ApiClient.chatbot.stats.get({ params });
      this.loading = false;
      if (response.status === 200) {
        this.stats = response.data.data;
      }
      else {
        this.stats = []
      }
    },
    refreshStats() {
      this.fetchStats();
    },
    changeDateRange(range) {
      this.selectedRange = range;
      this.fetchStats();
    },
    calculateTrend(metric) {
      console.log(metric)
      /*if (!this.previousStats || !this.stats) return null;

      const current = this.stats[metric];
      const previous = this.previousStats[metric];

      if (!previous || previous === 0) return null;

      const trend = ((current - previous) / previous) * 100;
      return Math.round(trend);*/
    },
    startAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }

      if (this.autoRefresh) {
        this.refreshInterval = setInterval(() => {
          this.fetchStats();
        }, 30000); // Refresh every 30 seconds
      }
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    exportToCSV() {
      if (!this.stats) return;

      // Prepare CSV data
      const csvData = [
        ['Metric', 'Value'],
        ['Active Conversations', this.stats.activeConversations],
        ['Takeovers Today', this.stats.takenOverToday],
        ['Converted Today', this.stats.convertedToday],
        ['Closed Today', this.stats.closedToday],
        ['Average Duration (minutes)', this.stats.averageDurationMinutes],
        ['High Intent Conversations', this.stats.highIntentConversations],
      ];

      // Convert to CSV string
      const csvContent = csvData.map(row => row.join(',')).join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `chatbot-stats-${this.selectedRange}-${dateStr}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
  created() {
    this.fetchStats();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  watch: {
    autoRefresh(newVal) {
      if (newVal) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.auto-refresh-indicator {
  display: flex;
  align-items: center;

  .blink {
    animation: blink 2s infinite;
  }
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

</style>
